// Owner minutes (step E62, migration 20261202090000, decision 014): how long people spend working in Ogma.
//
// What is measured, and nothing else: how many seconds a person is active on a page of the app, and on each task or
// approval from opening it to deciding it. "Active" means the page is visible and they pressed a key, clicked,
// scrolled or touched within the last 90 seconds; after that the clock stops, so an abandoned tab never counts.
// Only the durations, the task's id, the page (without ids or anything after "?") and the day are sent. No keys, no
// text, no mouse positions and nothing on the screen is recorded (docs/COMPLIANCE.md).
//
// This file is pure: the clock takes `now` as a function, so tests drive it. lib/ownerTimeTracker.tsx wires it to the
// browser, and crm.recordOwnerTime sends batches through act (record_owner_time).
import type { JsonObject } from "./types";

/** Stop counting this long after the last key, click, scroll or touch. */
export const IDLE_MS = 90_000;
/** Send what has built up at least this often while someone is using the app. */
export const FLUSH_EVERY_MS = 5 * 60_000;
/** A decided task is sent within this long, with whatever page time has built up. */
export const DECIDED_FLUSH_MS = 15_000;
/** The database's caps (crm_record_owner_time): an entry for a task, an entry for a page. */
export const TASK_ENTRY_MAX_S = 1200;
export const PAGE_ENTRY_MAX_S = 900;
export const MAX_ENTRIES = 50;

export type OwnerModule = "attract" | "find" | "network" | "connect" | "reconnect" | "setup" | "shared";

export const MODULE_LABELS: Record<OwnerModule, string> = {
  attract: "Attract",
  find: "Find",
  network: "Network",
  connect: "Connect",
  reconnect: "ReConnect",
  setup: "Set-up",
  shared: "Updates and records",
};

const UUID_SEG = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g;

/**
 * A page as it is kept: lower case, ids and anything after "?" or "#" removed, at most three parts, only letters,
 * numbers, "-", "_" and "/". "/contacts/companies/<id>?tab=x" becomes "/contacts/companies". The database does the same
 * (crm__owner_time_page).
 */
export function pagePath(pathname: string): string {
  const base = (pathname.split("?")[0] ?? "").split("#")[0] ?? "";
  const parts = base.toLowerCase().replace(UUID_SEG, "").split("/").filter(Boolean).slice(0, 3);
  const p = "/" + parts.map((x) => x.replace(/[^a-z0-9_-]/g, "")).filter(Boolean).join("/");
  return p.length > 81 ? p.slice(0, 81) : p;
}

/** The module a page belongs to, as crm__owner_time_module decides it for a page (migration 20270228140000). */
export function pageModule(page: string): OwnerModule {
  if (/^\/attract(\/|$)/.test(page)) return "attract";
  if (/^\/(find|prospects|campaigns)(\/|$)/.test(page) || /^\/(results\/outreach|contacts\/find)(\/|$)/.test(page)) return "find";
  if (/^\/network(\/|$)/.test(page)) return "network";
  if (/^\/(pipeline|conversations|quotes)(\/|$)/.test(page) || /^\/results\/sales(\/|$)/.test(page)) return "connect";
  if (/^\/reconnect(\/|$)/.test(page)) return "reconnect";
  if (/^\/(setup|onboarding|settings|website)(\/|$)/.test(page) || /^\/company\/(playbook|plays)(\/|$)/.test(page)) return "setup";
  return "shared";
}

/** What the app sends (act record_owner_time). */
export type TimeEntry =
  | { scope: "page"; page: string; seconds: number; day: string }
  | { scope: "task"; task: string; seconds: number; decided: boolean; page: string; day: string };

/** The person's own day, YYYY-MM-DD, in their browser's time zone. */
export function localDay(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface TaskTally {
  ms: number;
  decided: boolean;
  page: string;
}

/**
 * Counts active, visible time. Feed it what happens (input, visibility, page, a task opened, closed or decided) and
 * it credits the time since the last thing to whatever was in front of the person: the open task, else the page.
 * `drain` hands back whole seconds as entries and keeps the remainder.
 */
export class ActivityClock {
  private lastTick: number;
  private lastInput: number;
  private visible: boolean;
  private page = "/";
  private task: string | null = null;
  private pages = new Map<string, number>();
  private tasks = new Map<string, TaskTally>();

  constructor(private readonly now: () => number, visible = true) {
    this.lastTick = now();
    this.lastInput = this.lastTick;
    this.visible = visible;
  }

  /** Credit the time since the last call, up to 90 seconds after the last input, while the page is visible. */
  tick(): void {
    const t = this.now();
    if (this.visible && t > this.lastTick) {
      const until = Math.min(t, this.lastInput + IDLE_MS);
      const credit = Math.max(0, until - this.lastTick);
      if (credit > 0) {
        if (this.task) {
          const k = this.tasks.get(this.task) ?? { ms: 0, decided: false, page: this.page };
          k.ms += credit;
          this.tasks.set(this.task, k);
        } else {
          this.pages.set(this.page, (this.pages.get(this.page) ?? 0) + credit);
        }
      }
    }
    this.lastTick = Math.max(this.lastTick, t);
  }

  /** A key, click, scroll or touch: only that it happened, never what it was. */
  input(): void {
    this.tick();
    this.lastInput = this.now();
  }

  setVisible(visible: boolean): void {
    this.tick();
    this.visible = visible;
    if (visible) this.lastInput = this.now();
  }

  setPage(pathname: string): void {
    this.tick();
    this.page = pagePath(pathname);
    this.lastInput = this.now();
  }

  openTask(id: string): void {
    this.tick();
    this.task = id;
    if (!this.tasks.has(id)) this.tasks.set(id, { ms: 0, decided: false, page: this.page });
    this.lastInput = this.now();
  }

  closeTask(id: string): void {
    this.tick();
    if (this.task === id) this.task = null;
  }

  /** The task was decided: its time so far is final and goes with the next batch. */
  decide(id: string): void {
    this.tick();
    const k = this.tasks.get(id) ?? { ms: 0, decided: false, page: this.page };
    k.decided = true;
    this.tasks.set(id, k);
    if (this.task === id) this.task = null;
  }

  /** Whether a decided task is waiting to be sent. */
  hasDecided(): boolean {
    for (const k of this.tasks.values()) if (k.decided) return true;
    return false;
  }

  /** The time built up, as entries of whole seconds (each within the database's caps); the remainder is kept. */
  drain(): TimeEntry[] {
    this.tick();
    const day = localDay(this.now());
    const out: TimeEntry[] = [];
    for (const [id, k] of [...this.tasks]) {
      let s = Math.floor(k.ms / 1000);
      if (s < 1 && !k.decided) continue;
      k.ms -= s * 1000;
      if (k.decided) s = Math.max(s, 1);
      // a long stretch goes as several entries, each within the cap
      while (s > TASK_ENTRY_MAX_S) {
        out.push({ scope: "task", task: id, seconds: TASK_ENTRY_MAX_S, decided: false, page: k.page, day });
        s -= TASK_ENTRY_MAX_S;
      }
      out.push({ scope: "task", task: id, seconds: s, decided: k.decided, page: k.page, day });
      if (k.decided || (id !== this.task && k.ms < 1000)) this.tasks.delete(id);
    }
    for (const [page, ms] of [...this.pages]) {
      let s = Math.floor(ms / 1000);
      if (s < 1) continue;
      this.pages.set(page, ms - s * 1000);
      while (s > 0) {
        const n = Math.min(s, PAGE_ENTRY_MAX_S);
        out.push({ scope: "page", page, seconds: n, day });
        s -= n;
      }
    }
    return out.slice(0, MAX_ENTRIES);
  }
}

// ---------------------------------------------------------------------------
// The weekly figure (crm_owner_minutes)
// ---------------------------------------------------------------------------
export type Trend = "down" | "up" | "flat" | "new" | "none";

export interface OwnerMinutes {
  week_start: string;
  week_end: string;
  time_zone: string;
  this_week: {
    minutes: number;
    measured_minutes: number;
    estimated_minutes: number;
    task_minutes: number;
    decided: number;
    decided_outside: number;
    minutes_per_decision: number | null;
    people_count: number;
    active_days: number;
  };
  last_week: { minutes: number; estimated_minutes: number; decided: number; minutes_per_decision: number | null };
  trend: Trend;
  change_minutes: number;
  change_pct: number | null;
  by_module: { module: OwnerModule; label: string; minutes: number; last_week_minutes: number }[];
  by_kind: { kind: string; step_id: string | null; module: OwnerModule; minutes: number; estimated_minutes: number; decisions: number; minutes_per_decision: number | null }[];
  by_day: { day: string; minutes: number }[];
  people: { who: string; you: boolean; minutes: number; estimated_minutes: number; tasks_timed: number }[];
  weeks: { week_start: string; minutes: number }[];
}

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : Number(v) || 0);
const numOrNull = (v: unknown): number | null => (v === null || v === undefined || v === "" ? null : Number.isFinite(Number(v)) ? Number(v) : null);
const arr = (v: unknown): JsonObject[] => (Array.isArray(v) ? (v.filter((x) => x && typeof x === "object") as JsonObject[]) : []);
const MODULES = Object.keys(MODULE_LABELS) as OwnerModule[];
const asModule = (v: unknown): OwnerModule => (MODULES.includes(v as OwnerModule) ? (v as OwnerModule) : "shared");

/** crm_owner_minutes, with every field present and numeric. */
export function readOwnerMinutes(raw: unknown): OwnerMinutes {
  const r = (raw && typeof raw === "object" ? raw : {}) as JsonObject;
  const tw = (r.this_week ?? {}) as JsonObject;
  const lw = (r.last_week ?? {}) as JsonObject;
  const trend = ["down", "up", "flat", "new", "none"].includes(String(r.trend)) ? (r.trend as Trend) : "none";
  return {
    week_start: String(r.week_start ?? ""),
    week_end: String(r.week_end ?? ""),
    time_zone: String(r.time_zone ?? "Europe/London"),
    this_week: {
      minutes: num(tw.minutes),
      measured_minutes: num(tw.measured_minutes),
      estimated_minutes: num(tw.estimated_minutes),
      task_minutes: num(tw.task_minutes),
      decided: num(tw.decided),
      decided_outside: num(tw.decided_outside),
      minutes_per_decision: numOrNull(tw.minutes_per_decision),
      people_count: num(tw.people_count),
      active_days: num(tw.active_days),
    },
    last_week: { minutes: num(lw.minutes), estimated_minutes: num(lw.estimated_minutes), decided: num(lw.decided), minutes_per_decision: numOrNull(lw.minutes_per_decision) },
    trend,
    change_minutes: num(r.change_minutes),
    change_pct: numOrNull(r.change_pct),
    by_module: arr(r.by_module).map((m) => ({ module: asModule(m.module), label: String(m.label ?? MODULE_LABELS[asModule(m.module)]), minutes: num(m.minutes), last_week_minutes: num(m.last_week_minutes) })),
    by_kind: arr(r.by_kind).map((k) => ({
      kind: String(k.kind ?? ""),
      step_id: k.step_id == null ? null : String(k.step_id),
      module: asModule(k.module),
      minutes: num(k.minutes),
      estimated_minutes: num(k.estimated_minutes),
      decisions: num(k.decisions),
      minutes_per_decision: numOrNull(k.minutes_per_decision),
    })),
    by_day: arr(r.by_day).map((d) => ({ day: String(d.day ?? ""), minutes: num(d.minutes) })),
    people: arr(r.people).map((p) => ({ who: String(p.who ?? ""), you: p.you === true, minutes: num(p.minutes), estimated_minutes: num(p.estimated_minutes), tasks_timed: num(p.tasks_timed) })),
    weeks: arr(r.weeks).map((w) => ({ week_start: String(w.week_start ?? ""), minutes: num(w.minutes) })),
  };
}

/** Minutes in plain words: "45 min", "1 h 05 min", "under a minute". */
export function minutesText(m: number): string {
  if (!Number.isFinite(m) || m <= 0) return "0 min";
  if (m < 1) return "under a minute";
  const total = Math.round(m);
  if (total < 60) return `${total} min`;
  const h = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${h} h ${String(rest).padStart(2, "0")} min` : `${h} h`;
}

/** The trend against last week in plain words. Fewer minutes is better: Ogma doing more, the owner less. */
export function trendText(o: Pick<OwnerMinutes, "trend" | "change_minutes" | "change_pct" | "last_week">): string {
  const diff = minutesText(Math.abs(o.change_minutes));
  switch (o.trend) {
    case "down":
      return `${diff} less than last week${o.change_pct !== null ? ` (${Math.abs(o.change_pct)}% down)` : ""}`;
    case "up":
      return `${diff} more than last week${o.change_pct !== null ? ` (${o.change_pct}% up)` : ""}`;
    case "flat":
      return `About the same as last week (${minutesText(o.last_week.minutes)})`;
    case "new":
      return "The first week with time recorded";
    default:
      return "No time recorded yet";
  }
}
